import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { api } from "../utils/apiClient";
import { useAuth } from "./AuthContext";
import { useServiceAcceptanceCheck } from "./useServiceAcceptanceCheck";

const LegalAcceptanceContext = createContext(null);

export function LegalAcceptanceProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acceptanceModalOpen, setAcceptanceModalOpen] = useState(false);
  const [acceptedDocuments, setAcceptedDocuments] = useState(new Set());
  const [loadError, setLoadError] = useState(null);
  const [serviceCheckLoading, setServiceCheckLoading] = useState(false);
  const acceptedDocumentsRef = useRef(new Set());
  const serviceAccessMapRef = useRef({});
  const serviceCheckInFlightRef = useRef(new Map());
  const acceptanceResolverRef = useRef(null);

  useEffect(() => {
    acceptedDocumentsRef.current = acceptedDocuments;
  }, [acceptedDocuments]);

  // Fetch document content by document id
  const fetchDocumentContent = useCallback(async (id) => {
    try {
      const { data, ...rest } = await api.get(
        `/api/legal-documents/${id}/content`,
      );

      if (!data.ok) {
        throw new Error(`HTTP ${rest.status}`);
      }
      return data?.content;
    } catch (err) {
      throw new Error(`Unable to load legal document content for id: ${id}.`);
    }
  }, []);

  const queuePendingDocument = useCallback((documentWithContent) => {
    setLoadError(null);
    setPendingDocuments((prev) => {
      const exists = prev.some((doc) => doc.id === documentWithContent.id);
      if (exists) {
        return prev.map((doc) =>
          doc.id === documentWithContent.id ? documentWithContent : doc,
        );
      }
      return [...prev, documentWithContent];
    });
  }, []);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      setAcceptedDocuments(new Set());
      setPendingDocuments([]);
      setAcceptanceModalOpen(false);
      setLoadError(null);
      serviceAccessMapRef.current = {};
      serviceCheckInFlightRef.current = new Map();
    }
  }, [authLoading, user]);

  // A pending requestServiceAcceptance() must never hang: when the modal closes
  // without acceptance (dismissed, logout), resolve it with false.
  useEffect(() => {
    if (acceptanceModalOpen || !acceptanceResolverRef.current) return;
    const resolve = acceptanceResolverRef.current;
    acceptanceResolverRef.current = null;
    resolve(false);
  }, [acceptanceModalOpen]);

  const acceptDocument = async (docId) => {
    setLoading(true);
    try {
      await api.post("/api/legal-acceptance/accept", {
        legal_documents_id: docId,
      });
      setAcceptedDocuments((prev) => new Set([...prev, docId]));
      setPendingDocuments((prev) => prev.filter((doc) => doc.id !== docId));

      // Close modal if no more pending documents
      if (pendingDocuments.length === 1) {
        setAcceptanceModalOpen(false);
      }
      if (acceptanceResolverRef.current) {
        acceptanceResolverRef.current(true);
        acceptanceResolverRef.current = null;
      }
    } catch (err) {
      console.error("Failed to accept document:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const runAcceptanceCheck = useServiceAcceptanceCheck({
    user,
    authLoading,
    fetchDocumentContent,
    queuePendingDocument,
    acceptedDocumentsRef,
    serviceAccessMapRef,
    serviceCheckInFlightRef,
    acceptanceResolverRef,
    setServiceCheckLoading,
    setLoadError,
    setAcceptedDocuments,
    setAcceptanceModalOpen,
  });

  const ensureServicePathAccepted = useCallback(
    (servicePath) => runAcceptanceCheck(servicePath, false),
    [runAcceptanceCheck],
  );

  // Show the T&C modal now and resolve with true once the user accepts.
  // Used by per-request services to force acceptance on every request.
  const requestServiceAcceptance = useCallback(
    (servicePath) => runAcceptanceCheck(servicePath, true),
    [runAcceptanceCheck],
  );

  const acceptAllDocuments = async () => {
    setLoading(true);
    try {
      for (const doc of pendingDocuments) {
        await api.post("/api/legal-acceptance/accept", {
          legal_documents_id: doc.id,
        });
        setAcceptedDocuments((prev) => new Set([...prev, doc.id]));
      }
      setPendingDocuments([]);
      setAcceptanceModalOpen(false);
      if (acceptanceResolverRef.current) {
        acceptanceResolverRef.current(true);
        acceptanceResolverRef.current = null;
      }
    } catch (err) {
      console.error("Failed to accept documents:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <LegalAcceptanceContext.Provider
      value={{
        pendingDocuments,
        acceptanceModalOpen,
        setAcceptanceModalOpen,
        loading,
        acceptDocument,
        acceptAllDocuments,
        acceptedDocuments,
        loadError,
        user,
        ensureServicePathAccepted,
        requestServiceAcceptance,
        serviceCheckLoading,
      }}
    >
      {children}
    </LegalAcceptanceContext.Provider>
  );
}

export function useLegalAcceptance() {
  const context = useContext(LegalAcceptanceContext);
  if (!context) {
    throw new Error(
      "useLegalAcceptance must be used within LegalAcceptanceProvider",
    );
  }
  return context;
}
